
import { mergeMapTo, distinctUntilChanged, skip, filter, mergeMap, mapTo } from 'rxjs/operators'
import { of, empty } from 'rxjs'
import { destroy, stopSubmit } from 'redux-form'
import { post } from 'helpers/observableAjaxHelpers'
import { ActionsObservable, combineEpics } from 'redux-observable'
import { CHECK_EMAIL_EXISTENCE, closeOnboardingActionCreator } from 'actions/onboardingActions'
import { SET_MEMBER_INFO } from 'actions/memberActions'
import { CHECK_EMAIL_EXISTENCE_URL } from 'interfaces/ajax'
import { Epic } from 'interfaces/middleware'
import { Action } from 'actions'
import { FORM_ONBOARDING_PROPERTY, FORM_ONBOARDING_REGISTER } from 'constants/forms'
import resolveErrorById, { TYPE_EMAIL_EXISTS } from 'constants/errors'
import { AjaxError } from 'rxjs/ajax'
import { VALIDATION_ERROR_STATUS_CODE } from 'constants/ajax'
import { PAGE_CHANGED, setUnexpectedErrorActionCreator } from 'actions/globalActions'
import { getErrorType } from 'helpers/errorHelpers'
import { Store } from 'interfaces/state'
import { ONBOARDING_SUMMARY_URL, ONBOARDING_PROPERTY_URL } from 'helpers/tangleSharedHelpers'
import { isOnboardingEndedSelector } from 'selectors/onboardingSelectors'
import { byType } from 'rxOperators/byType'
import { catchSafe } from 'rxOperators/catchSafe'

// HELPERS
const onboardingStepChanged = (action$: ActionsObservable<Action>) =>
  action$
    .pipe(byType(SET_MEMBER_INFO)).pipe(
      distinctUntilChanged(
        (next, prev) => prev.payload.onboarding_step === next.payload.onboarding_step,
      ),
      // we need only changes
      skip(1))

// EPICS

const destroyFormsOnFinishEpic: Epic = (action$, store) =>
  action$
    .pipe(byType(SET_MEMBER_INFO)).pipe(
      filter((a) => a.payload.onboarding_step === 'finished'),
      mergeMap(() => {
        const formsState = store.getState().form
        const actions: Action[] = []

        // only if forms exists
        if (formsState[FORM_ONBOARDING_PROPERTY]) {
          actions.push(destroy(FORM_ONBOARDING_PROPERTY))
        }

        return of(...actions)
      }))

export const finishOnboardingEpic: Epic = (action$, store: Store, { remote }) =>
  action$.pipe(
    filter((action) => {
      if (action.type === PAGE_CHANGED) {
        const state = store.getState()
        const isOnboardingComplete = isOnboardingEndedSelector(state)
        const isOnSummaryPage = action.payload.prev === ONBOARDING_PROPERTY_URL && action.payload.next === ONBOARDING_SUMMARY_URL
        if (isOnboardingComplete && isOnSummaryPage) {
          remote.sendAnalyticsEvent('kochava', { name: 'Onboarding_Completed', data: {}})
        }
      }
      return false
    }))

export const closeOnboardingEpic: Epic = (action$, store: Store) =>
  action$.pipe(
    filter((action) => {
      if (action.type === SET_MEMBER_INFO && action.payload.onboarding_step === 'finished') {
        return true
      }

      if (action.type === PAGE_CHANGED) {
        const state = store.getState()
        return action.payload.prev === ONBOARDING_SUMMARY_URL && isOnboardingEndedSelector(state)
      }

      return false
    }),
    mapTo(closeOnboardingActionCreator))

const checkEmailExistence: Epic = (action$) => action$.pipe(
  byType(CHECK_EMAIL_EXISTENCE),
  mergeMap(({ payload }) => post(CHECK_EMAIL_EXISTENCE_URL, { body: payload }).pipe(
    mergeMapTo(empty()),
    catchSafe((error: AjaxError) => {
      const { status } = error
      if (status === VALIDATION_ERROR_STATUS_CODE) {
        return of<Action>(stopSubmit(FORM_ONBOARDING_REGISTER, { email: resolveErrorById(TYPE_EMAIL_EXISTS) }))
      }
      return of<Action>(setUnexpectedErrorActionCreator(getErrorType(error), error))
    })
  ),
  )
)

const onboardingEpic = combineEpics(
  destroyFormsOnFinishEpic,
  finishOnboardingEpic,
  closeOnboardingEpic,
  checkEmailExistence,
)

export {
  onboardingEpic as default,
  onboardingStepChanged,
}
